import {Component, computed, OnInit, signal} from '@angular/core';
import { NgClass } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MatFormField } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatInput } from '@angular/material/input';
import { ConfirmationComponent } from 'src/app/pages/auth/confirmation/confirmation.component';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { LoginWithPasswordComponent } from 'src/app/pages/auth/login-with-password/login-with-password.component';
import { AuthService } from 'src/app/shared/services/auth.service';
import { HttpClient } from '@angular/common/http';
import {
  CountryInterface,
  ErrorInterface,
} from 'src/app/shared/interfaces/global';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { startWith } from 'rxjs';
import { environment } from 'src/environments/environment';
import { MatSelectSearchComponent } from 'ngx-mat-select-search';
import { toSignal } from "@angular/core/rxjs-interop";
import {AuthStep} from "../../../shared/interfaces/auth";
import {NewtelComponent} from "../newtel/newtel.component";

@Component({
  selector: 'app-login',
  imports: [
    NgClass,
    TranslateModule,
    MatFormField,
    MatOption,
    MatInput,
    ConfirmationComponent,
    FormsModule,
    LoginWithPasswordComponent,
    ReactiveFormsModule,
    MatSelect,
    MatSelectSearchComponent,
    NewtelComponent,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
  standalone: true,
})
export class LoginComponent implements OnInit {
  loginType = signal<0 | 1>(0); //0-почта, 1-телефон, 2 - когда забыли пароль
  loginStep = signal<AuthStep>('login');
  lang = signal<string>('ru');
  is_recovery = this.$auth.is_recovery;
  readonly phone: FormControl<string | null> = new FormControl('', {
    validators: [
      Validators.required,
      Validators.minLength(12),
      Validators.maxLength(15),
    ],
    updateOn: 'change',
  });
  // selectedCountry = new FormControl(1);
  readonly email: FormControl<string | null> = new FormControl('', {
    validators: [Validators.required, Validators.pattern('^\\S+@\\S+\\.\\S+$')],
    updateOn: 'change',
  });
  countryList: CountryInterface[] = [];
  getCodeError = false;
  newPassword = false;
  selectedCountry = new FormControl(1);
  countryListFilter = new FormControl('', { nonNullable: true });
  errorMessage: ErrorInterface | null = null;

  constructor(
    public $t: TranslateService,
    private $auth: AuthService,
    private $http: HttpClient,
    private $router: Router,
    private route: ActivatedRoute,
    private $dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.$dialog.closeAll();
    this.hasToken();
    this.getCountryList();
    this.lang.set(this.$t.getDefaultLang());
    this.phone.valueChanges.subscribe((value) => {
      this.phone.setValue(this.formatPhoneNumber(value ?? ''), {
        emitEvent: false,
      });
    });
  }

  countryListFiltered = computed(()=> {
    const search = (this.$countryControlsChanges() || '').toLowerCase();
    if (!search) {
      return [...this.countryList];
    }
    return this.countryList.filter((country) =>
      country.name.toLowerCase().includes(search),
    );
  });
  $countryControlsChanges = toSignal<string>(this.countryListFilter.valueChanges.pipe(startWith('')));

  hasToken() {
    const url = new URL(location.href.replace(/%3D/g, '=').replace(/%3F/g, '&').replace(/%26/g, '&').replace(/%2F/g, ''));

    const returnURL = url.searchParams.get('returnURL') || url.searchParams.get('returnUrl');
    const accessToken_param = url.searchParams.get('access_token');
    const refreshToken_param = url.searchParams.get('refresh_token');
    if (returnURL || accessToken_param) {

      if (accessToken_param && refreshToken_param) {
        this.$auth.setToken(accessToken_param, refreshToken_param);
      }
      this.$router.navigateByUrl(returnURL!).then();
    }
  }

  getCountryList() {
    this.$http
      .get<{ items: CountryInterface[] }>('reference/countries')
      .subscribe((res) => {
        this.countryList = res.items;
        this.countryListFilter.setValue(' ');
      });
  }

  changeLang(lang: string) {
    this.lang.set(lang);
    localStorage.setItem('lang', lang);
    this.$t.setDefaultLang(lang);
  }

  changeLoginType(type: 0 | 1) {
    this.loginType.set(type);
    this.phone.setValue('');
    this.email.setValue('');
    this.phone.setErrors(null);
    this.email.setErrors(null);
    this.getCodeError = false;
    this.email.markAsUntouched();
    this.phone.markAsUntouched();
    this.errorMessage = null;
  }

  showConfirmation(show: any): void {
    if (show) {
      this.loginStep.set('confirmation');
    } else {
      this.loginStep.set('login');
    }
  }

  showConfirmationFromPassword(login: string) {
    this.is_recovery.set(true);
    this.loginStep.set('confirmation')
    if (login.includes('@')) {
      this.email.setValue(login);
      this.loginType.set(0);
    } else {
      this.phone.setValue(login);
      this.loginType.set(1);
    }
  }

  showLoginWithPassword() {
    this.loginStep.set('withPassword');
  }

  formatPhoneNumber(value: string): string {
    if (this.getSelectedCountryId() === 1 && value.length === 13) {
      value = value.replace('+78', '+7');
    }
    if (
      this.getSelectedCountryId() === 1 &&
      value.replace(/\D/g, '').length === 12
    ) {
      if (value.replace(/\D/g, '').startsWith('78')) {
        value = '+7' + value.replace(/\D/g, '').substring(2);
      }
    }
    let phoneCode = this.countryList
      ?.find((el) => el.country_id === this.getSelectedCountryId())
      ?.code_phone.replace('-', '');
    if (phoneCode === undefined) phoneCode = '7';
    if (value === phoneCode || value.length === 0) return phoneCode;
    if (value.startsWith(phoneCode)) {
      value = value.substring(phoneCode.length);
    }
    const digitsOnly = value.replace(/\D/g, '');
    if (digitsOnly.startsWith(phoneCode)) {
      return digitsOnly.replace(phoneCode, phoneCode).slice(0, 15);
    } else if (digitsOnly.startsWith(phoneCode.replace('+', ''))) {
      let val = digitsOnly;
      if (digitsOnly.length >= phoneCode.length)
        val = digitsOnly.substring(phoneCode.length - 1);
      return digitsOnly.startsWith(phoneCode.replace('+', ''))
        ? (phoneCode + val).slice(0, 15)
        : (phoneCode + digitsOnly).slice(0, 15);
    } else {
      if (value === phoneCode.slice(0, -1)) return phoneCode;
      return (phoneCode + digitsOnly).slice(0, 15);
    }
  }

  getErrorMessage(): string {
    if (this.getCodeError) return 'auth.getCodeError';
    if (this.loginType() === 0) {
      if (this.email.hasError('required')) {
        return 'auth.mail_error_required';
      }
      if (this.email.hasError('pattern')) {
        return 'auth.mail_error_valid';
      }
    } else {
      return 'auth.phone_error_valid';
    }

    return '';
  }

  getCode() {
    this.$auth
      .getCodeAuth(
        !!this.loginType(),
        this.loginType() ? this.phone.value : this.email.value,
        this.getSelectedCountryId()!,
      )
      .subscribe({
        next: (res) => {
          this.$auth.setTempToken(res.temporary_token);
          this.loginStep.set('confirmation');
          return true;
        },
        error: (res: { error: ErrorInterface }) => {
          console.log(res);
          this.errorMessage = res.error;
        },
      });
  }

  getCountryPhoneCode(): string {
    return (
      this.countryList?.find(
        (el) => el.country_id === this.getSelectedCountryId(),
      )?.code_phone || ''
    );
  }

  routeToCreatePassword() {
    this.loginStep.set('withPassword');
    this.newPassword = true;
  }

  routeToNewTel() {
    this.loginStep.set('newTel');
  }

  getSocialLink(social: string) {
    const language = this.lang() === 'ru' ? 1 : 2;
    return `${environment.ssoHost}auth/${social}/login?country_id=${this.getSelectedCountryId()}&language_id=${language}`;
  }

  getSelectedCountryId() {
    return this.selectedCountry.value;
  }

  showBackBtn= computed(() => {
    return this.loginStep() !== 'login';
  });
}
